import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, Eye, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { UnifiedFilePreview } from '@/components/shared/UnifiedFilePreview';

interface LocationDocumentsPreviewProps {
  locationId: string;
  onViewAll?: () => void;
}

const VISIBILITY_TAGS = [
  { value: 'intern', label: 'Intern', color: 'bg-red-500' },
  { value: 'extern', label: 'Extern', color: 'bg-orange-500' },
  { value: 'publiek', label: 'Publiek', color: 'bg-green-500' },
];

export function LocationDocumentsPreview({ locationId, onViewAll }: LocationDocumentsPreviewProps) {
  const [previewFile, setPreviewFile] = useState<any | null>(null);

  const { data: documents = [], isLoading } = useQuery({
    queryKey: ['location-documents-preview', locationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('location_documents')
        .select('id, document_name, description, visibility_tag, file_size, storage_path, created_at')
        .eq('location_id', locationId)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      return data || [];
    },
  });

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getVisibilityBadge = (tag: string | null) => {
    if (!tag) return null;
    const config = VISIBILITY_TAGS.find(t => t.value === tag);
    if (!config) return null;
    return (
      <Badge className={`${config.color} text-white text-xs`}>
        {config.label}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Documenten
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Laden...</p>
        </CardContent>
      </Card>
    );
  }

  if (documents.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Documenten
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Geen documenten geüpload</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Documenten
            <Badge variant="secondary" className="ml-1">{documents.length}</Badge>
          </CardTitle>
          {onViewAll && (
            <Button variant="ghost" size="sm" onClick={onViewAll} className="text-xs">
              Alle bekijken
              <ChevronRight className="w-3 h-3 ml-1" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {documents.map((doc: any) => (
          <div 
            key={doc.id} 
            className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer group"
            onClick={() => setPreviewFile({ 
              file_name: doc.document_name, 
              file_size: doc.file_size,
              bucket: 'location-documents',
              storage_path: doc.storage_path
            })}
          >
            <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium truncate">{doc.document_name}</p>
                {getVisibilityBadge(doc.visibility_tag)}
              </div>
              {doc.description && (
                <p className="text-xs text-muted-foreground truncate">{doc.description}</p>
              )}
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="opacity-0 group-hover:opacity-100 transition-opacity h-7 w-7 p-0"
              onClick={(e) => {
                e.stopPropagation();
                setPreviewFile({ 
                  file_name: doc.document_name, 
                  file_size: doc.file_size,
                  bucket: 'location-documents',
                  storage_path: doc.storage_path
                });
              }}
            >
              <Eye className="w-3 h-3" />
            </Button>
          </div>
        ))}

        {/* Preview Modal */}
        {previewFile && (
          <UnifiedFilePreview
            file={{
              name: previewFile.file_name,
              bucket: previewFile.bucket,
              path: previewFile.storage_path,
              size: previewFile.file_size,
            }}
            open={!!previewFile}
            onOpenChange={(open) => !open && setPreviewFile(null)}
            portal="charging"
          />
        )}
      </CardContent>
    </Card>
  );
}